import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { CheckSquare, Plus, Filter, Calendar, Trash2, Pencil } from 'lucide-react';
import CollaboratorPicker from '@/components/CollaboratorPicker';
import CollaboratorChips from '@/components/CollaboratorChips';

const STATUSES = ['To Do', 'In Progress', 'Review', 'Done'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const STATUS_COLOR = {
  'To Do': 'border-amber-500/40 text-amber-200',
  'In Progress': 'border-sky-500/40 text-sky-300',
  'Review': 'border-purple-500/40 text-purple-300',
  'Done': 'border-emerald-500/40 text-emerald-300',
};
const PRIO_COLOR = {
  Low: 'border-slate-500/40 text-slate-300',
  Medium: 'border-sky-500/40 text-sky-300',
  High: 'border-orange-500/40 text-orange-300',
  Urgent: 'border-red-500/40 text-red-300',
};

const EMPTY_FORM = { title: '', description: '', assignee_id: '', sop_id: '', deadline: '', priority: 'Medium', status: 'To Do', collaborators: [] };

export default function TasksPage() {
  const { user, hasPerm } = useAuth();
  const canCreate = hasPerm('task_create');
  const canEdit = hasPerm('task_edit');
  const canDelete = hasPerm('task_delete');

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [sops, setSops] = useState([]);
  const [mine, setMine] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  // Set of task IDs whose descriptions are currently expanded (Read more clicked)
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (taskId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  // Roughly two full lines of the small text used on cards. Descriptions longer than
  // this are truncated with a "Read more" toggle so cards stay uniform in height.
  const DESC_TRUNCATE_THRESHOLD = 120;

  // Create dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Edit dialog
  const [editing, setEditing] = useState(null); // holds the task being edited
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const load = async () => {
    const params = new URLSearchParams();
    if (mine) params.set('mine', 'true');
    if (statusFilter !== 'all') params.set('status', statusFilter);
    const res = await api.get('/tasks' + (params.toString() ? `?${params}` : ''));
    setTasks(res.data);
  };

  useEffect(() => { load(); }, [mine, statusFilter]);
  useEffect(() => {
    api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
    api.get('/sops').then((r) => setSops(r.data)).catch(() => {});
  }, []);

  const updateStatus = async (t, status) => {
    await api.patch(`/tasks/${t.id}`, { status });
    load();
  };

  const remove = async (t) => {
    if (!confirm(`Delete task "${t.title}"?`)) return;
    await api.delete(`/tasks/${t.id}`);
    load();
  };

  const createTask = async () => {
    const payload = { ...form };
    if (!payload.sop_id) delete payload.sop_id;
    if (!payload.deadline) delete payload.deadline;
    await api.post('/tasks', payload);
    setOpen(false);
    setForm(EMPTY_FORM);
    load();
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditForm({
      title: t.title || '',
      description: t.description || '',
      assignee_id: t.assignee_id || '',
      sop_id: t.sop_id || '',
      deadline: t.deadline ? String(t.deadline).slice(0, 10) : '',
      priority: t.priority || 'Medium',
      status: t.status || 'To Do',
      collaborators: Array.isArray(t.collaborators) ? t.collaborators : [],
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const payload = { ...editForm };
    // Clear empty optional fields — backend treats null as "remove"
    if (!payload.sop_id) payload.sop_id = null;
    if (!payload.deadline) payload.deadline = null;
    await api.patch(`/tasks/${editing.id}`, payload);
    setEditing(null);
    load();
  };

  // A task is editable if the user has `task_edit` (Owner/Admin auto-pass) OR they created it.
  const canEditTask = (t) => canEdit || t.created_by === user?.user_id || t.created_by === user?.id;

  const grouped = useMemo(() => {
    const g = { 'To Do': [], 'In Progress': [], 'Review': [], 'Done': [] };
    tasks.forEach((t) => { (g[t.status] = g[t.status] || []).push(t); });
    return g;
  }, [tasks]);

  return (
    <div>
      <PageHeader
        icon={CheckSquare}
        title="Tasks"
        subtitle="Daily assignments linked to SOPs."
        actions={canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-task-btn" className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold"><Plus className="h-4 w-4 mr-1" /> New Task</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-white">Assign new task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="task-title" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Assignee</Label>
                    <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="task-assignee"><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Linked SOP</Label>
                    <Select value={form.sop_id || '_none'} onValueChange={(v) => setForm({ ...form, sop_id: v === '_none' ? '' : v })}>
                      <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Select SOP" /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">
                        <SelectItem value="_none">None</SelectItem>
                        {sops.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Deadline</Label><Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
                  <div><Label>Priority</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0a1628] border-amber-500/20">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0a1628] border-amber-500/20">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div>
                  <Label>Collaborators <span className="text-[10px] text-slate-500">(shared work — they see it in “My tasks”)</span></Label>
                  <CollaboratorPicker
                    users={users}
                    value={form.collaborators || []}
                    onChange={(next) => setForm({ ...form, collaborators: next })}
                    exclude={form.assignee_id ? [form.assignee_id] : []}
                    testIdPrefix="task-collab"
                  />
                </div>
              </div>
              <DialogFooter><Button onClick={createTask} disabled={!form.title || !form.assignee_id} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="task-submit">Assign task</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <Card className="shine-card p-3 rounded-xl mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-amber-300" />
          <Button data-testid="filter-mine" size="sm" variant={mine ? 'default' : 'outline'} onClick={() => setMine(true)} className={mine ? 'bg-amber-500 text-[#0a1628] hover:bg-amber-400' : 'border-amber-500/40 text-amber-200'}>My tasks</Button>
          <Button data-testid="filter-all" size="sm" variant={!mine ? 'default' : 'outline'} onClick={() => setMine(false)} className={!mine ? 'bg-amber-500 text-[#0a1628] hover:bg-amber-400' : 'border-amber-500/40 text-amber-200'}>All tasks</Button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Label className="text-xs text-slate-400">Status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9 bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0a1628] border-amber-500/20">
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="grid lg:grid-cols-4 gap-4">
        {STATUSES.map((col) => (
          <div key={col}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-white text-sm uppercase tracking-wider">{col}</h3>
              <Badge variant="outline" className={STATUS_COLOR[col]}>{(grouped[col] || []).length}</Badge>
            </div>
            <div className="space-y-2 min-h-[100px]">
              {(grouped[col] || []).length === 0 ? (
                <div className="text-xs text-slate-500 italic px-2">—</div>
              ) : (grouped[col] || []).map((t) => (
                <Card key={t.id} className="shine-card p-3 rounded-lg" data-testid={`task-card-${t.id}`}>
                  <div className="font-semibold text-white text-sm">{t.title}</div>
                  {t.description && (() => {
                    const isLong = t.description.length > DESC_TRUNCATE_THRESHOLD;
                    const isExpanded = expandedIds.has(t.id);
                    return (
                      <div className="mt-1">
                        <p
                          className={`text-xs text-slate-400 whitespace-pre-wrap ${isLong && !isExpanded ? 'line-clamp-2' : ''}`}
                          data-testid={`task-desc-${t.id}`}
                        >
                          {t.description}
                        </p>
                        {isLong && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(t.id)}
                            className="text-[11px] text-amber-300 hover:text-amber-200 font-semibold mt-0.5 focus:outline-none focus-visible:underline"
                            data-testid={`task-toggle-desc-${t.id}`}
                          >
                            {isExpanded ? 'Show less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {t.sop_title && <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-200">SOP: {t.sop_title.split(' ')[0]}…</Badge>}
                    <Badge variant="outline" className={`text-[10px] ${PRIO_COLOR[t.priority]}`}>{t.priority}</Badge>
                  </div>
                  <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
                    <span>👤 {t.assignee_name}</span>
                    {t.deadline && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(t.deadline).toLocaleDateString()}</span>}
                  </div>
                  {Array.isArray(t.collaborators) && t.collaborators.length > 0 && (
                    <div className="mt-1.5">
                      <CollaboratorChips ids={t.collaborators} users={users} size="xs" />
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-1">
                    <Select value={t.status} onValueChange={(v) => updateStatus(t, v)}>
                      <SelectTrigger className="h-7 text-xs bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0a1628] border-amber-500/20">
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {canEditTask(t) && (
                      <Button size="icon" variant="ghost" onClick={() => openEdit(t)} className="h-7 w-7 text-sky-300 hover:bg-sky-500/10" data-testid={`task-edit-${t.id}`} title="Edit task"><Pencil className="h-3.5 w-3.5" /></Button>
                    )}
                    {canDelete && (
                      <Button size="icon" variant="ghost" onClick={() => remove(t)} className="h-7 w-7 text-red-400 hover:bg-red-500/10" data-testid={`task-delete-${t.id}`} title="Delete task"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Task Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Edit task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="bg-[#0f2240] border-amber-500/20" data-testid="edit-task-title" /></div>
            <div><Label>Description</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assignee</Label>
                <Select value={editForm.assignee_id} onValueChange={(v) => setEditForm({ ...editForm, assignee_id: v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20" data-testid="edit-task-assignee"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Linked SOP</Label>
                <Select value={editForm.sop_id || '_none'} onValueChange={(v) => setEditForm({ ...editForm, sop_id: v === '_none' ? '' : v })}>
                  <SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue placeholder="Select SOP" /></SelectTrigger>
                  <SelectContent className="bg-[#0a1628] border-amber-500/20">
                    <SelectItem value="_none">None</SelectItem>
                    {sops.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Deadline</Label><Input type="date" value={editForm.deadline} onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })} className="bg-[#0f2240] border-amber-500/20" /></div>
              <div><Label>Priority</Label><Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}><SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0a1628] border-amber-500/20">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Status</Label><Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}><SelectTrigger className="bg-[#0f2240] border-amber-500/20"><SelectValue /></SelectTrigger><SelectContent className="bg-[#0a1628] border-amber-500/20">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div>
              <Label>Collaborators</Label>
              <CollaboratorPicker
                users={users}
                value={editForm.collaborators || []}
                onChange={(next) => setEditForm({ ...editForm, collaborators: next })}
                exclude={editForm.assignee_id ? [editForm.assignee_id] : []}
                testIdPrefix="edit-task-collab"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-500/40 text-slate-300" data-testid="edit-task-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={!editForm.title || !editForm.assignee_id} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628]" data-testid="edit-task-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
